import { SubscriptionLineItemData } from '@modules/subscription/CreateSubscriptionPage/SubscriptionForm';
import { R } from '@core/utils/r';
import {
  ChangeSubscriptionLineResponse,
  ChangeSubscriptionResponse,
  ChangeSubscriptionUpdatedItem,
  TransformedChangeSubscriptionLineResponse,
} from '@business/entities/project';
import { Formatter } from '@core/utils/formatter';
import { InvoiceDiscountType } from '@business/entities/TransactionInvoice';
import { InvoiceSalesTaxData } from '@modules/common/InvoiceSalesTax';
import { InvoiceDiscountData } from '@modules/common/InvoiceDiscount';
import { TaxService } from '../tax_service';

export function lineTotal(item: SubscriptionLineItemData): number {
  if (!item) return 0;
  if (item.packageItems.length > 0) {
    return item.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(lineTotal(line)), 2), 0);
  } else {
    return R.roundDecimal(item.qty * item.rate, 2);
  }
}

export function allLineItemsTotal(items: SubscriptionLineItemData[]): number {
  return items.reduce((total, item) => R.roundDecimal(total + lineTotal(item), 2), 0);
}

export function totalLineQty(items: SubscriptionLineItemData): number {
  if (!items) return 0;
  if (items.packageItems.length > 0) {
    return items.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(totalLineQty(line)), 2), 0);
  } else {
    return R.toNumber(items.qty);
  }
}

export function totalLineRate(items: SubscriptionLineItemData): number {
  if (!items) return 0;
  if (items.packageItems.length > 0) {
    return items.packageItems.reduce((total, line) => R.roundDecimal(total + R.toNumber(totalLineRate(line)), 2), 0);
  } else {
    return items.rate;
  }
}

const displayAbleSubscriptionHeaderKeys = [
  //   'SubAmount',
  'Title',
  'Cycle',
  'ContactEmail',
  'Contact',
  'SaleRep',
  'StartDate',
  //   'Quantity',
  'Address',
  'Message',
  'TotalAmount',
  'Balance',
  'Memo',
  'Discount',
  'SalesTax',
];

function parsedSubscriptionItemValue(key: string, value: any) {
  if (key === 'SubAmount' || key === 'TotalAmount' || key === 'Balance') {
    return Formatter.formatCurrency(value, 'USD');
  }

  if (key === 'StartDate') {
    return Formatter.formatTime(value, 'MM/DD/YYYY');
  }

  if (key === 'Discount') {
    if (!value) return '';
    const isDiscount = R.get(value, 'isDiscount');
    if (isDiscount == 'true') {
      const type = R.get(value, 'type');
      if (type === 'percentage') {
        return `${R.get(value, 'value')}%`;
      } else {
        return `$${R.get(value, 'value')}`;
      }
    }
    return '';
  }

  if (key === 'SalesTax') {
    const isSalesTax = R.get(value, 'isSalesTax');
    if (isSalesTax === 'true') {
      return `${R.get(value, 'value')}%`;
    }
    return '';
  }

  return value;
}

function isSubscriptionItemValueChanged(item: ChangeSubscriptionResponse, key: string) {
  const oldValue = R.get(item, `old${key}`);
  const newValue = R.get(item, `new${key}`);

  if (!oldValue && !newValue) {
    return false;
  }

  if ((!oldValue && newValue) || (oldValue && !newValue)) {
    return true;
  }

  if (key === 'Contact') {
    return oldValue !== newValue;
  }

  if (key === 'Address') {
    return (
      oldValue.address !== newValue.address || oldValue.city !== newValue.city || oldValue.state !== newValue.state || oldValue.zip !== newValue.zip
    );
  }

  if (key === 'Discount') {
    return oldValue.isDiscount !== newValue.isDiscount || oldValue.type !== newValue.type || oldValue.value !== newValue.value;
  }

  if (key === 'SalesTax') {
    return oldValue.isManual !== newValue.isManual || oldValue.isSalesTax !== newValue.isSalesTax || oldValue.value !== newValue.value;
  }

  return oldValue !== newValue;
}

function subscriptionItemTitle(key: string) {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function subscriptionItemVisibility(key: string, oldValue: any, newValue: any) {
  if (!oldValue && !newValue) {
    return false;
  }

  return true;
}

export function compareChangeSubscriptionItems(item: ChangeSubscriptionResponse): ChangeSubscriptionUpdatedItem[] {
  const allItems = [] as ChangeSubscriptionUpdatedItem[];

  R.keys(item).forEach(i => {
    if (R.includes(i, 'old')) {
      const oldItemKey = i.replace('old', '');
      const newItemKey = 'new' + oldItemKey;
      const oldValue = R.get(item, i);
      const newValue = R.get(item, newItemKey);

      const updatedOldValue = parsedSubscriptionItemValue(oldItemKey, oldValue);
      const updatedNewValue = parsedSubscriptionItemValue(oldItemKey, newValue);
      const isStats = oldItemKey === 'TotalAmount' || oldItemKey === 'Balance';
      const showOldValueOnNew = isStats && R.isNull(newValue);

      allItems.push({
        key: oldItemKey,
        title: subscriptionItemTitle(oldItemKey),
        isValueChanged: showOldValueOnNew ? false : isSubscriptionItemValueChanged(item, oldItemKey),
        oldValue: updatedOldValue,
        newValue: showOldValueOnNew ? updatedOldValue : updatedNewValue,
        show: subscriptionItemVisibility(oldItemKey, oldValue, newValue),
      });
    }
  });

  return allItems.filter(item => displayAbleSubscriptionHeaderKeys.includes(item.key));
}

export function transformSubscriptionLines(lines: ChangeSubscriptionLineResponse[]): TransformedChangeSubscriptionLineResponse[] {
  return lines.map(line => {
    const oldProduct = line.oldProduct;
    const newProduct = line.newProduct;
    const oldService = line.oldService;
    const newService = line.newService;
    const oldPackage = line.oldPackage;
    const newPackage = line.newPackage;

    const oldTitle = oldProduct ? oldProduct.title : oldService ? oldService.title : oldPackage ? oldPackage.title : '';
    const newTitle = newProduct ? newProduct.title : newService ? newService.title : newPackage ? newPackage.title : '';

    return {
      ...line,
      oldTitle: oldTitle,
      newTitle: newTitle,
      oldTaxable: line.oldTaxable && R.toNumber(line.oldTaxable) === 1 ? 'Yes' : 'No',
      newTaxable: line.newTaxable && R.toNumber(line.newTaxable) === 1 ? 'Yes' : 'No',

      //   oldDescription: line.oldDescription,
      //   newDescription: line.newDescription,
      //   oldQty: line.oldQty,
      //   newQty: line.newQty,
      //   oldUnitPrice: line.oldUnitPrice,
      //   newUnitPrice: line.newUnitPrice,
      //   oldSaleRep: line.oldSaleRep,
      //   newSaleRep: line.newSaleRep,
      //   oldSaleRep2: line.oldSaleRep2,
      //   newSaleRep2: line.newSaleRep2,
      //   oldTaxable: line.oldTaxable,
      //   newTaxable: line.newTaxable,
    };
  });
}

export function isSubscriptionLineValueChanged(oldValue: string | number, newValue: string | number) {
  if (!oldValue && !newValue) {
    return false;
  }

  if ((!oldValue && newValue) || (oldValue && !newValue)) {
    return true;
  }

  return oldValue !== newValue;
}

export function getSubTotalNumber(formValue: SubscriptionLineItemData): number {
  if (!formValue) return 0;
  if (formValue.packageItems && formValue.packageItems.length > 0) {
    return formValue.packageItems.reduce((acc, item) => {
      const total = acc + item.qty * item.rate;
      return R.isNaN(total) ? 0 : total;
    }, 0);
  } else {
    const total = formValue.qty * formValue.rate;
    return R.isNaN(total) ? 0 : total;
  }
}

export function discountableRows(items: SubscriptionLineItemData[]): SubscriptionLineItemData[] {
  // Subscription line items don't have isDiscount property, so we return all items
  return items.map(item => {
    if (item.packageItems && item.packageItems.length > 0) {
      return { ...item, packageItems: item.packageItems.filter(() => true) };
    }
    return item;
  });
}

export function calculateGrandTotalDiscount(items: SubscriptionLineItemData[], editable = true, discount: InvoiceDiscountData): number {
  const t = items.reduce((acc, item) => acc + getSubTotalNumber(item), 0);
  return getDiscountAmount(discount, t);
}

export function getDiscountAmount(discount: InvoiceDiscountData, amount: number): number {
  if (discount.type === InvoiceDiscountType.percentage) {
    return (amount * discount.value) / 100;
  } else {
    return discount.value ?? 0;
  }
}

export function getTaxAmt(items: SubscriptionLineItemData[], salesTax: InvoiceSalesTaxData, discount: InvoiceDiscountData) {
  const allLineItems = items;
  const allPackageItems = R.flatten(allLineItems.map(l => l.packageItems));
  let taxableItems = [
    ...allLineItems.map(o => {
      return { ...o, packageItems: [] };
    }),
    ...allPackageItems.map(o => {
      return { ...o, packageItems: [] };
    }),
  ];

  taxableItems = taxableItems.filter(l => {
    if (!l.service) return true;
    return !l.service.includes('package');
  });

  const parsedItems = taxableItems.map(l => ({ taxable: l.taxable, subtotal: getSubTotalNumber(l) }));

  let discountAmt = 0;
  if (discount.isDiscount) {
    const discountType = discount.type;
    const discValue = discount.value;
    if (discountType === 'fixed') {
      discountAmt = R.toNumber(discValue);
    } else {
      const filteredData = discountableRows(R.cloneDeep(items));
      discountAmt = calculateGrandTotalDiscount(filteredData, true, discount);
    }
    discount = {
      ...discount,
      type: InvoiceDiscountType.fixed,
      value: discountAmt,
    };
  }

  // Cast discount to include category for TaxService compatibility
  const taxDiscount = {
    ...discount,
    category: '' as any, // Subscriptions don't have category, but TaxService expects it
  };

  return TaxService.totalTaxAmt(parsedItems, taxDiscount as any, salesTax, true);
}
